import { z } from "zod";
export const memoryScopeTypeSchema = z.enum(["user", "channel", "organisation"]);
export const memoryKindSchema = z.enum([
    "preference",
    "decision",
    "correction",
    "context",
    "entity_hint",
]);
export const memoryTypeSchema = z.enum(["claim", "procedure"]);
export const memoryAuthoritySchema = z.enum([
    "user",
    "reviewer",
    "tool",
    "assistant",
    "inference",
]);
export const memoryStatusSchema = z.enum([
    // `pending` remains readable during the rolling migration to the explicit
    // proposal and approval states below.
    "pending",
    "proposed",
    "pending_approval",
    "active",
    "superseded",
    "disputed",
    "retracted",
    "archived",
]);
const memoryIdentifierSchema = z.string().trim().min(1).max(500);
const memoryTimestampSchema = z.number().int().nonnegative();
const applicabilityValuesSchema = z.array(z.string().trim().min(1).max(500)).max(100);
export const memoryApplicabilitySchema = z.object({
    databaseIds: applicabilityValuesSchema.optional(),
    datasetIds: applicabilityValuesSchema.optional(),
    entityTypes: applicabilityValuesSchema.optional(),
    entityIds: applicabilityValuesSchema.optional(),
    schemaFingerprints: applicabilityValuesSchema.optional(),
    metrics: applicabilityValuesSchema.optional(),
    operations: applicabilityValuesSchema.optional(),
}).strict();
export const memoryVerificationStatusSchema = z.enum([
    "unverified",
    "verified",
    "rejected",
]);
export const memoryVerificationSchema = z.object({
    status: memoryVerificationStatusSchema,
    verifiedBy: memoryIdentifierSchema.optional(),
    verifiedAt: memoryTimestampSchema.optional(),
}).strict().superRefine((verification, context) => {
    const hasActor = verification.verifiedBy !== undefined;
    const hasTimestamp = verification.verifiedAt !== undefined;
    if (hasActor !== hasTimestamp) {
        context.addIssue({
            code: "custom",
            message: "verifiedBy and verifiedAt must be provided together",
            path: hasActor ? ["verifiedAt"] : ["verifiedBy"],
        });
    }
    if (verification.status === "unverified" && (hasActor || hasTimestamp)) {
        context.addIssue({
            code: "custom",
            message: "unverified memories cannot carry verification attribution",
            path: ["status"],
        });
    }
    if (verification.status !== "unverified" && (!hasActor || !hasTimestamp)) {
        context.addIssue({
            code: "custom",
            message: "reviewed verification states require an actor and timestamp",
            path: ["status"],
        });
    }
});
export const memoryAuthoringVerificationSchema = z.object({
    status: z.literal("unverified"),
}).strict();
export const memoryVersionIdSchema = z.string().trim().min(1).max(2_048);
export const memorySnapshotOriginSchema = z.enum([
    "baseline",
    "created",
    "revision",
]);
/**
 * Stable identity for an immutable version snapshot. The server remains the
 * authority for assigning the next version number; callers cannot supply this
 * identifier through mutation inputs.
 */
export function buildMemoryVersionId(memoryId, version) {
    const canonicalMemoryId = memoryIdentifierSchema.parse(memoryId);
    const canonicalVersion = z.number().int().positive().parse(version);
    return memoryVersionIdSchema.parse(`memory-version:${canonicalMemoryId}:${canonicalVersion}`);
}
function validityWindowIsOrdered(value) {
    return value.validFrom === undefined ||
        value.validTo === undefined ||
        value.validTo > value.validFrom;
}
export const memoryEntrySchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1).optional(),
    scopeType: memoryScopeTypeSchema,
    scopeId: z.string().trim().min(1),
    memoryKey: z.string().trim().min(1),
    kind: memoryKindSchema,
    summary: z.string().trim().min(1),
    value: z.record(z.string(), z.unknown()),
    source: z.string().trim().min(1),
    status: memoryStatusSchema,
    confidence: z.number().min(0).max(1).optional(),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
    version: z.number().int().positive(),
    // Additive during the rolling migration from mutable current rows to
    // append-only versions. New writers populate these fields; old rows and old
    // control-plane payloads remain readable until the fleet converges.
    memoryType: memoryTypeSchema.optional(),
    authority: memoryAuthoritySchema.optional(),
    applicability: memoryApplicabilitySchema.optional(),
    validFrom: memoryTimestampSchema.optional(),
    validTo: memoryTimestampSchema.optional(),
    observedAt: memoryTimestampSchema.optional(),
    ingestedAt: memoryTimestampSchema.optional(),
    verification: memoryVerificationSchema.optional(),
    modifiedBy: memoryIdentifierSchema.optional(),
    versionId: memoryVersionIdSchema.optional(),
}).refine(validityWindowIsOrdered, {
    message: "validTo must be greater than validFrom",
    path: ["validTo"],
}).refine((entry) => entry.versionId === undefined ||
    entry.versionId === buildMemoryVersionId(entry.id, entry.version), {
    message: "versionId must be derived from id and version",
    path: ["versionId"],
});
export const memoryVersionSchema = z.object({
    versionId: memoryVersionIdSchema,
    memoryId: memoryIdentifierSchema,
    previousVersionId: memoryVersionIdSchema.optional(),
    snapshotOrigin: memorySnapshotOriginSchema,
    tenantSlug: memoryIdentifierSchema,
    organisationId: memoryIdentifierSchema.optional(),
    scopeType: memoryScopeTypeSchema,
    scopeId: memoryIdentifierSchema,
    memoryKey: z.string().trim().min(1).max(200),
    memoryType: memoryTypeSchema,
    kind: memoryKindSchema,
    summary: z.string().trim().min(1).max(500),
    value: z.record(z.string(), z.unknown()),
    source: memoryIdentifierSchema,
    authority: memoryAuthoritySchema,
    status: memoryStatusSchema,
    applicability: memoryApplicabilitySchema,
    confidence: z.number().min(0).max(1).optional(),
    sourceSessionId: memoryIdentifierSchema.optional(),
    sourceRunId: memoryIdentifierSchema.optional(),
    createdBy: memoryIdentifierSchema,
    modifiedBy: memoryIdentifierSchema,
    createdAt: memoryTimestampSchema,
    updatedAt: memoryTimestampSchema,
    recordedAt: memoryTimestampSchema,
    validFrom: memoryTimestampSchema,
    validTo: memoryTimestampSchema.optional(),
    observedAt: memoryTimestampSchema,
    ingestedAt: memoryTimestampSchema,
    verification: memoryVerificationSchema,
    version: z.number().int().positive(),
}).strict().refine(validityWindowIsOrdered, {
    message: "validTo must be greater than validFrom",
    path: ["validTo"],
}).refine((snapshot) => snapshot.versionId === buildMemoryVersionId(snapshot.memoryId, snapshot.version), {
    message: "versionId must be derived from memoryId and version",
    path: ["versionId"],
}).superRefine((snapshot, context) => {
    const expectedPreviousVersionId = snapshot.version > 1
        ? buildMemoryVersionId(snapshot.memoryId, snapshot.version - 1)
        : undefined;
    const needsPreviousVersion = snapshot.snapshotOrigin === "revision";
    if (needsPreviousVersion &&
        (snapshot.version <= 1 || snapshot.previousVersionId !== expectedPreviousVersionId)) {
        context.addIssue({
            code: "custom",
            message: "revision snapshots must reference the immediately preceding version",
            path: ["previousVersionId"],
        });
    }
    if (!needsPreviousVersion && snapshot.previousVersionId !== undefined) {
        context.addIssue({
            code: "custom",
            message: "baseline and created snapshots cannot reference a previous version",
            path: ["previousVersionId"],
        });
    }
    if (snapshot.snapshotOrigin === "created" && snapshot.version !== 1) {
        context.addIssue({
            code: "custom",
            message: "created snapshots must be version 1",
            path: ["version"],
        });
    }
    if (snapshot.scopeType === "organisation" && snapshot.organisationId === undefined) {
        context.addIssue({
            code: "custom",
            message: "organisation-scoped versions require organisationId",
            path: ["organisationId"],
        });
    }
});
export const memoryRelationTypeSchema = z.enum([
    "supports",
    "derived_from",
    "example_of",
    "corrects",
    "supersedes",
    "contradicts",
]);
export const memoryRelationSchema = z.object({
    id: memoryIdentifierSchema,
    tenantSlug: memoryIdentifierSchema,
    organisationId: memoryIdentifierSchema.optional(),
    sourceVersionId: memoryVersionIdSchema,
    targetVersionId: memoryVersionIdSchema,
    relationType: memoryRelationTypeSchema,
    metadata: z.record(z.string(), z.unknown()).default({}),
    createdBy: memoryIdentifierSchema,
    createdAt: memoryTimestampSchema,
}).strict().refine((relation) => relation.sourceVersionId !== relation.targetVersionId, {
    message: "a memory version relation must connect distinct versions",
    path: ["targetVersionId"],
});
export const listMemoriesQuerySchema = z.object({
    scopeType: memoryScopeTypeSchema.optional(),
    scopeId: z.string().trim().min(1).optional(),
    status: memoryStatusSchema.default("active"),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const memoryLifecycleIdempotencyKeySchema = z.string().trim().min(8).max(160)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u, "Expected an idempotency key");
export const upsertMemoryInputSchema = z.object({
    confirmation: z.literal(true),
    idempotencyKey: memoryLifecycleIdempotencyKeySchema,
    expectedVersion: z.literal(0),
    scopeType: memoryScopeTypeSchema,
    scopeId: z.string().trim().min(1),
    memoryKey: z.string().trim().min(1).max(200),
    memoryType: memoryTypeSchema.default("claim"),
    kind: memoryKindSchema,
    summary: z.string().trim().min(1).max(500),
    value: z.record(z.string(), z.unknown()).default({}),
    applicability: memoryApplicabilitySchema.default({}),
    confidence: z.number().min(0).max(1).optional(),
    validFrom: memoryTimestampSchema.optional(),
    validTo: memoryTimestampSchema.optional(),
    observedAt: memoryTimestampSchema.optional(),
    verification: memoryAuthoringVerificationSchema.default({ status: "unverified" }),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
}).strict().superRefine((input, refinement) => {
    if (input.sourceRunId && !input.sourceSessionId) {
        refinement.addIssue({
            code: "custom",
            path: ["sourceRunId"],
            message: "sourceRunId requires sourceSessionId",
        });
    }
    if (!validityWindowIsOrdered(input)) {
        refinement.addIssue({
            code: "custom",
            path: ["validTo"],
            message: "validTo must be greater than validFrom",
        });
    }
});
export const listMemoriesResponseSchema = z.object({
    memories: z.array(memoryEntrySchema),
    /**
     * Whether the caller may add a memory to this scope now: authoring is on
     * for the deployment and the caller can write here. Optional only during
     * the tenant/app mixed-version rollout window; absent means not reported.
     */
    canCreate: z.boolean().optional(),
});
export const memoryResponseSchema = z.object({ memory: memoryEntrySchema });
export const memoryPathParamsSchema = z.object({ memoryId: memoryIdentifierSchema.optional() }).strict();
